import { createFileRoute } from "@tanstack/react-router";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { NewGearDialog } from "@/components/forms/new-gear-dialog";
import { gear } from "@/mocks/data";

export const Route = createFileRoute("/gear")({
  head: () => ({
    meta: [
      { title: "Gear — Studio OS" },
      { name: "description", content: "Camera bodies, lenses, lighting, and support inventory." },
    ],
  }),
  component: GearPage,
});

const groups = ["body", "lens", "lighting", "support", "audio", "other"] as const;
const groupLabels: Record<(typeof groups)[number], string> = {
  body: "Camera bodies", lens: "Lenses", lighting: "Lighting",
  support: "Support & rigs", audio: "Audio", other: "Other",
};

function GearPage() {
  return (
    <div className="mx-auto max-w-7xl px-6 py-8 lg:px-10 lg:py-10">
      <PageHeader
        title="Gear"
        description={`${gear.length} items tracked across ${new Set(gear.map((g) => g.category)).size} categories.`}
        actions={<NewGearDialog />}
      />
      <div className="space-y-8">
        {groups.map((cat) => {
          const items = gear.filter((g) => g.category === cat);
          if (items.length === 0) return null;
          return (
            <section key={cat}>
              <div className="mb-3 flex items-baseline justify-between">
                <h2 className="font-mono text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                  {groupLabels[cat]}
                </h2>
                <span className="text-[11px] text-muted-foreground">{items.length} items</span>
              </div>
              <div className="overflow-hidden rounded-xl bg-card ring-1 ring-border">
                <table className="w-full text-left text-sm">
                  <tbody className="divide-y divide-border">
                    {items.map((g) => (
                      <tr key={g.id}>
                        <td className="px-4 py-3 font-medium">{g.name}</td>
                        <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{g.serial ?? "—"}</td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">
                          Last used {g.lastUsed ?? "—"}
                        </td>
                        <td className="px-4 py-3 text-right"><StatusBadge status={g.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
